import { api } from "@/shared/api";
import { formatStartsIn, formatTimeRange } from "@/shared/format";
import { queryKeys } from "@/shared/query";
import { Button } from "@/shared/ui/primitives";
import { type AppointmentDto, appointmentReminderAt } from "@repo/polaris-shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BellRing } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "polaris:appointment-reminders:dismissed";
const TICK_MS = 30_000;

/** A dismissed reminder stays dismissed until its appointment ends; a moved one comes back. */
function reminderKey(appointment: Pick<AppointmentDto, "id" | "startsAt">) {
  return `${appointment.id}:${appointment.startsAt}`;
}

function readDismissed(now: number): Record<string, number> {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}") as Record<
      string,
      unknown
    >;
    return Object.fromEntries(
      Object.entries(stored).filter(
        (entry): entry is [string, number] => typeof entry[1] === "number" && entry[1] > now,
      ),
    );
  } catch {
    return {};
  }
}

function writeDismissed(value: Record<string, number>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private windows: the reminder comes back at the next reload, nothing else breaks.
  }
}

/** The reminders due now, soonest first: from `reminderMinutes` before the start to the end. */
export function dueReminders(
  appointments: readonly AppointmentDto[],
  now: number,
  dismissed: Record<string, number>,
): AppointmentDto[] {
  return appointments
    .filter((appointment) => {
      if (appointment.status !== "scheduled" || dismissed[reminderKey(appointment)]) return false;
      const at = appointmentReminderAt({
        startsAt: new Date(appointment.startsAt),
        reminderMinutes: appointment.reminderMinutes,
      });
      return at !== null && at.getTime() <= now && new Date(appointment.endsAt).getTime() > now;
    })
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt));
}

/**
 * The appointments still ahead or under way: once one ends it leaves the chat,
 * and its outcome is asked in Oggi and in the calendar.
 */
export function upcomingAppointments(
  appointments: readonly AppointmentDto[],
  now: number,
): AppointmentDto[] {
  return appointments.filter(
    (appointment) =>
      appointment.status === "scheduled" && new Date(appointment.endsAt).getTime() > now,
  );
}

/**
 * The reminder of my next appointments while Polaris is open (ADR 0059): a
 * banner above the page and the count in the tab title. Without an open tab
 * nothing arrives, by choice for now.
 */
export function AppointmentReminders({
  workspaceSlug,
  timeZone,
}: {
  workspaceSlug: string;
  timeZone: string;
}) {
  const reminders = useQuery({
    queryKey: queryKeys.appointmentReminders(workspaceSlug),
    queryFn: () => api.appointments.reminders(workspaceSlug),
    // The next 24 hours: refreshed by the events, and now and then for the window,
    // also in a tab left in the background, where the reminder matters most.
    refetchInterval: 10 * 60_000,
    refetchIntervalInBackground: true,
  });
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(() => readDismissed(Date.now()));

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  const due = useMemo(
    () => dueReminders(reminders.data ?? [], now, dismissed),
    [reminders.data, now, dismissed],
  );

  useEffect(() => {
    if (due.length === 0) return;
    const original = document.title;
    document.title = `(${due.length}) ${original}`;
    return () => {
      document.title = original;
    };
  }, [due.length]);

  const first = due[0];
  if (!first) return null;
  const dismiss = () => {
    const next = {
      ...readDismissed(Date.now()),
      [reminderKey(first)]: new Date(first.endsAt).getTime(),
    };
    writeDismissed(next);
    setDismissed(next);
  };
  return (
    <output className="appointment-reminder" aria-live="polite">
      <BellRing aria-hidden="true" size={18} />
      <span className="appointment-reminder__text">
        <strong>
          {formatStartsIn(first.startsAt, now).replace(/^./, (letter) => letter.toUpperCase())}:{" "}
          {first.title}
        </strong>
        <span>
          {formatTimeRange(first.startsAt, first.endsAt, timeZone)}
          {first.location ? ` · ${first.location}` : ""}
          {due.length > 1 ? ` · e altri ${due.length - 1}` : ""}
        </span>
      </span>
      {first.lead ? (
        <Link
          to="/w/$workspaceSlug/inbox/$leadId"
          params={{ workspaceSlug, leadId: first.lead.id }}
          className="button button--secondary button--small"
        >
          Apri chat
        </Link>
      ) : null}
      <Button size="small" variant="quiet" onClick={dismiss}>
        Ok
      </Button>
    </output>
  );
}
